import React, { forwardRef } from "react";
import { useFormContext, RegisterOptions } from "react-hook-form";
import cn from "classnames";
import Button from "storefront/components/Button";
import SmallTitle from "storefront/components/SmallTitle";
import ImageV2 from "storefront/components/ImageV2";
import PhotoUpload from "storefront/components/PhotoUpload";
import { useMergeRefs } from "storefront/hooks/useMergeRefs";
import { useFormControl } from "../FormControl";
import styles from "./PhotoInput.module.scss";

interface PhotoInputProps {
  rules?: RegisterOptions;
  className?: string;
  disabled?: boolean;
  maxSize?: number;
  dimensions?: {
    width: number;
    height: number;
  };
  placeholder?: string;
}

/**
 * PhotoInput component
 *
 * A form component for photo uploads that integrates with react-hook-form.
 * Shows a preview of the uploaded image and provides upload functionality.
 */
const PhotoInput = forwardRef<HTMLInputElement, PhotoInputProps>(
  (
    { rules, className, disabled, maxSize, dimensions, placeholder, ...props },
    ref,
  ) => {
    const {
      register,
      watch,
      setValue,
      formState: { isSubmitting },
    } = useFormContext();

    const { name, error } = useFormControl();

    const value = watch(name);

    const handleSuccess = (url: string) => {
      setValue(name, url, { shouldValidate: true, shouldDirty: true });
    };

    // We still need to register the field with react-hook-form
    // but we'll hide the actual input element
    const { ref: hookFormRef, ...registerProps } = register(name, rules);

    const mergedRef = useMergeRefs(hookFormRef, ref);

    return (
      <div
        className={cn(
          styles.photoInput,
          { [styles.hasError]: !!error },
          { [styles.disabled]: disabled || isSubmitting },
          className,
        )}
      >
        {/* Hidden input to register with react-hook-form */}
        <input
          id={name}
          ref={mergedRef}
          type="hidden"
          value={value || ""}
          {...registerProps}
          {...props}
        />

        <div className={styles.photoInputContainer}>
          <div className={styles.photoInputButton}>
            <PhotoUpload
              className={styles.photoInputField}
              onSuccess={handleSuccess}
            />

            <Button
              variant="secondary"
              size="medium"
              disabled={disabled || isSubmitting}
            >
              {value ? "Replace Photo" : "Upload Photo"}
            </Button>
          </div>

          {dimensions || maxSize ? (
            <SmallTitle className={styles.photoInputRules}>
              {dimensions
                ? `Dimension ${dimensions.width}x${dimensions.height}. `
                : null}
              {maxSize ? `Max file size is ${maxSize}kb.` : null}
            </SmallTitle>
          ) : null}
        </div>

        {value || placeholder ? (
          <ImageV2
            className={styles.photoPreview}
            alt="preview"
            src={value || placeholder || ""}
            width={50}
            height={50}
            quality={90}
            fit="crop"
            rotate={0}
          />
        ) : null}
      </div>
    );
  },
);

PhotoInput.displayName = "PhotoInput";

export default PhotoInput;
